/**
 * The ARR pacing dial — the instrument-panel centerpiece. A 240° industrial
 * arc: app MRR track in red, lagged Accelerator track stacked in blue, the
 * on-pace marker as a needle tick, tick marks every 10%.
 */

export function Gauge(props: {
  actualApp: number;       // app (Apple) ARR, production
  actualAccel: number;     // Accelerator (Stripe) ARR, production
  expected: number;        // on-pace ARR for today
  target: number;
  centerLabel: string;     // e.g. "$0" actual
  subLabel: string;        // e.g. "of $1.00M target"
}) {
  const { target } = props;
  const START = -210; // degrees
  const SWEEP = 240;
  const R = 84;
  const C = 100;

  const frac = (v: number) => (target > 0 ? Math.min(Math.max(v / target, 0), 1) : 0);
  const polar = (deg: number, r: number) => {
    const rad = (deg * Math.PI) / 180;
    return [C + r * Math.cos(rad), C + r * Math.sin(rad)] as const;
  };
  const arc = (from: number, to: number, r: number) => {
    if (to - from < 0.1) return "";
    const [x1, y1] = polar(START + from, r);
    const [x2, y2] = polar(START + to, r);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };

  const appDeg = frac(props.actualApp) * SWEEP;
  const accelDeg = frac(props.actualApp + props.actualAccel) * SWEEP;
  const expectedDeg = frac(props.expected) * SWEEP;
  const [mx1, my1] = polar(START + expectedDeg, R - 12);
  const [mx2, my2] = polar(START + expectedDeg, R + 10);

  return (
    <svg viewBox="0 0 200 178" style={{ width: "100%", maxWidth: 360, display: "block", margin: "0 auto" }} role="img" aria-label="ARR pacing gauge">
      {/* bezel ticks */}
      {Array.from({ length: 11 }, (_, i) => {
        const d = START + (i / 10) * SWEEP;
        const [x1, y1] = polar(d, R + 4);
        const [x2, y2] = polar(d, R + (i % 5 === 0 ? 12 : 8));
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--faint)" strokeWidth={i % 5 === 0 ? 1.6 : 0.8} />;
      })}
      {/* track */}
      <path d={arc(0, SWEEP, R)} fill="none" stroke="var(--line)" strokeWidth="9" strokeLinecap="butt" />
      {/* accelerator (stacked, drawn first so app overlays from zero) */}
      <path d={arc(appDeg, accelDeg, R)} fill="none" stroke="var(--blue)" strokeWidth="9" />
      {/* app */}
      <path d={arc(0, appDeg, R)} fill="none" stroke="var(--red)" strokeWidth="9" />
      {/* on-pace needle */}
      <line x1={mx1} y1={my1} x2={mx2} y2={my2} stroke="var(--green)" strokeWidth="2" />
      {/* center readout */}
      <text x={C} y={C - 4} textAnchor="middle" fill="var(--ink)" fontFamily="var(--mono)" fontSize="26" fontWeight="600">
        {props.centerLabel}
      </text>
      <text x={C} y={C + 16} textAnchor="middle" fill="var(--faint)" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.5">
        {props.subLabel.toUpperCase()}
      </text>
    </svg>
  );
}
